import React, { useEffect, useState } from 'react';

import routePaths from 'config/routes';
import moment from 'moment';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { getDataCall } from 'services/api';
import apiPaths from 'services/apiPaths';
import { privateRoute } from 'services/privateRoute';
import { setBreadCrumbs, updateChallenges } from 'src/actions/appActions';
import { getBreadCrumbs, NotFound } from 'src/components';
import { builderChallenge } from 'src/components/challenge/builder/BuilderChallenge';
import BuildPageChallenge, {
  showMoreButton,
} from 'src/components/challenge/ChallengeBuildPage';
import ChallengeCard from 'src/components/challenge/ChallengeCard';
import ChallengeFilter from 'src/components/challenge/ChallengeFilter';
import { IRootReducers } from 'src/reducers';
import {
  ChallengedTabsEnum,
  ChallengeTypeEnum,
  PlatformEnums,
  ResourceTypeEnum,
} from 'src/shared/enums';
import {
  ChallengeCardButton,
  ChallengeDetail,
  IChallenge,
  IChallengeQuiz,
} from 'src/shared/models';
import { appTypes } from 'src/types/appTypes';
import {
  fetchInitialData,
  getTranslatedData,
  renderChallengeCard,
} from 'utils';
import { ReduxPageContext } from 'src/store';

const { GET_TRAININGS } = apiPaths.CHALLENGES;

interface OwnProps {
  initialData: IChallenge[];
}

type Props = OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

export const renderChallengeTraining = (
  challenge: ChallengeDetail
): JSX.Element => {
  const { formatMessage } = useIntl();
  const { challengeDates, challengeResources } = challenge;

  const challengeQuiz: IChallengeQuiz = challengeResources.find(
    ({ type }) => ResourceTypeEnum.QUIZ === type
  )?.config;

  let config: ChallengeDetail;
  let challengeCardButton: ChallengeCardButton = {
    path: routePaths.PAGES.TRAINING_DETAIL,
    title: formatMessage({ id: 'training.see' }),
    className: '',
    isDisabled: false,
  };

  switch (true) {
    case !challengeDates.startDate:
    case !challengeDates.endDate:
      challengeCardButton = {
        ...challengeCardButton,
        title: formatMessage({ id: 'training.not-available' }),
        className:
          'challengeCard__link challengeCard__link--inactive challengeCard__link--blocked',
      };
      break;
    case challengeQuiz?.isQuizDone:
      challengeCardButton = {
        ...challengeCardButton,
        title: formatMessage({ id: 'training.repeat' }),
        className: 'challengeCard__link challengeCard__link--active',
      };
      break;

    case moment(Date.now()).isBefore(challengeDates.startDate):
    case moment(Date.now()).isAfter(challengeDates.startDate):
      challengeCardButton = {
        ...challengeCardButton,
        title: formatMessage({ id: 'training.see' }),
        className: 'challengeCard__link challengeCard__link--active',
      };
      break;
  }

  config = {
    ...challenge,
    challengeName: getTranslatedData(challenge, 'name'),
    challengeDescription:
      getTranslatedData(challenge, 'description') ||
      getTranslatedData(challenge, 'descriptionLong'),
    challengeCardButton,
  };

  return <ChallengeCard {...config} />;
};

const TrainingPage: NextPage<Props> = ({
  setBreadCrumbs,
  trainingList,
  updateChallenges,
  user,
}): JSX.Element => {
  const router = useRouter();
  const [trainings, setTrainings] = useState<ChallengeDetail[]>();
  const [filters, setFilters] = useState<ChallengedTabsEnum>(
    ChallengedTabsEnum.ALL
  );

  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [requestPage, setRequestPage] = useState<number>(2); //Hemos pedido 8 y las páginas van de 4 en 4. Así que toca la pagina 2
  const [canShowMore, setCanShowMore] = useState<boolean>(
    trainingList.length >= 8
  );

  const { formatMessage } = useIntl();

  const handleShowMore = async () => {
    setIsLoadingMore(true);

    const _categories = '';

    // TODO: Add categorias filtradas
    // if (requestCategories.length > 0) {
    //   _categories = `&categories=${requestCategories.join()}`;
    // }

    const request = `${GET_TRAININGS}?page=${requestPage}&size=4${_categories}`;

    try {
      const ids = new Set(trainingList.map((training) => training.challengeId));
      const response = await getDataCall({
        dataPath: request,
        callConfig: {},
      });

      const newchallengeAcc: ChallengeDetail[] = [
        ...trainingList,
        ...response.data
          .filter((training: IChallenge) => !ids.has(training.idChallenge))
          .map((challenge: IChallenge) => builderChallenge(challenge, user)),
      ];

      updateChallenges({ trainings: newchallengeAcc });
      setRequestPage(requestPage + 1);
      setCanShowMore(response.data.length == 4);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderTrainings = () => {
    let filteredTrainings: ChallengeDetail[] = [];
    const customMessage =
      filters === ChallengedTabsEnum.COMPLETED
        ? 'page.training.no-completed-results'
        : null;

    if (!trainings?.length) return <NotFound message={customMessage} />;

    if (trainings && trainings.length) {
      switch (filters) {
        case ChallengedTabsEnum.PENDING:
          filteredTrainings = trainings.filter((_event) => {
            const { isChallengeInLife, isQuizDone } =
              _event.headboardConfiguration;
            return !isQuizDone && isChallengeInLife;
          });
          break;
        // TODO: use isCampaignCompleted and align
        case ChallengedTabsEnum.COMPLETED:
          filteredTrainings = trainings.filter(
            ({ challengeResources }) =>
              challengeResources.find(
                ({ type }) => ResourceTypeEnum.QUIZ === type
              )?.config.isQuizDone
          );
          break;
        case ChallengedTabsEnum.FAVORITES:
          filteredTrainings = trainings.filter(
            (_event) => _event.challengeLikes.liked
          );
          break;
        default:
          filteredTrainings = trainings;
          break;
      }
    }

    if (!filteredTrainings.length) return <NotFound message={customMessage} />;

    return filteredTrainings.map((challenge) =>
      renderChallengeCard({ challenge })
    );
  };

  useEffect(() => {
    const breadCrumbs = getBreadCrumbs(router, formatMessage);
    setTimeout(() => {
      setBreadCrumbs(breadCrumbs);
    }, 20);

    // clear breadcrumbs on unmount component
    return () => {
      // TODO: Remove setTimeout => check store.ts
      setTimeout(() => {
        setBreadCrumbs([]);
      }, 20);
    };
  }, []);

  const filterComponent: JSX.Element = (
    <ChallengeFilter
      challengeType={ChallengeTypeEnum.FORMATION}
      materialTypes={[]}
      setChallengeList={setTrainings}
      challengeList={trainingList}
    />
  );

  const showMoreComponent =
    filters == ChallengedTabsEnum.ALL
      ? showMoreButton(
          `page.marketing-materials.discover-more`,
          isLoadingMore,
          canShowMore,
          handleShowMore
        )
      : null;

  return (
    <>
      <BuildPageChallenge
        pageClassName="trainingPage"
        onChangeTab={setFilters}
        renderChallenges={renderTrainings}
        filterComponent={filterComponent}
        showMoreComponent={showMoreComponent}
        tabs={[
          { key: ChallengedTabsEnum.ALL, label: 'app.all' },
          { key: ChallengedTabsEnum.PENDING, label: 'app.pending' },
          { key: ChallengedTabsEnum.COMPLETED, label: 'app.completed' },
          { key: ChallengedTabsEnum.FAVORITES, label: 'app.my-favourites' },
        ]}
      />
    </>
  );
};

const initialAction = async (
  accessToken: string,
  ctx: ReduxPageContext
): Promise<{ data: unknown }> => {
  try {
    const challenges: IChallenge[] = await fetchInitialData(
      accessToken,
      `${GET_TRAININGS}?size=8`,
      ctx
    );

    const trainings: ChallengeDetail[] = challenges.map((challenge) =>
      builderChallenge(challenge, ctx.store.getState().auth.user)
    );

    if (trainings)
      ctx.store.dispatch({
        type: appTypes.APP_UPDATE_CHALLENGES,
        payload: { trainings },
      });

    return { data: challenges };
  } catch (error) {
    console.error('SERVER ERROR: ', error);
    return { data: null };
  }
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    trainingList: state.app.challenges.trainings,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ updateChallenges, setBreadCrumbs }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  privateRoute({
    WrappedComponent: TrainingPage,
    initialAction,
    platforms: [PlatformEnums.PIN],
  })
);
